import { useState } from "react"
import { trpc } from "~/lib/trpc"
import { Button, Input } from "@aix/ui"
import { X, Plus, Trash2, GripVertical } from "lucide-react"
import toast from "react-hot-toast"
import { normalizeUrl } from "./hooks/useOpenTabs"

export interface EditablePack {
  id: string
  slug: string
  name: string
  description?: string | null
  emoji?: string | null
  links: { url: string; label?: string }[]
}

interface LinkRow {
  url: string
  label: string
}

interface PackFormModalProps {
  /** Pass an existing pack to edit; omit to create a new one. */
  pack?: EditablePack
  onClose: () => void
  onSaved: () => void
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")

export function PackFormModal({ pack, onClose, onSaved }: PackFormModalProps) {
  const isEdit = Boolean(pack)

  const [name, setName] = useState(pack?.name ?? "")
  const [slug, setSlug] = useState(pack?.slug ?? "")
  const [slugTouched, setSlugTouched] = useState(isEdit)
  const [emoji, setEmoji] = useState(pack?.emoji ?? "")
  const [description, setDescription] = useState(pack?.description ?? "")
  const [links, setLinks] = useState<LinkRow[]>(
    pack?.links?.length ? pack.links.map((l) => ({ url: l.url, label: l.label ?? "" })) : [{ url: "", label: "" }],
  )

  const utils = trpc.useUtils()

  const onMutationSuccess = (message: string) => {
    toast.success(message)
    void utils.linkPacks.list.invalidate()
    if (pack) void utils.linkPacks.getBySlug.invalidate({ slug: pack.slug })
    onSaved()
  }

  const createMutation = trpc.linkPacks.create.useMutation({
    onSuccess: () => onMutationSuccess("Pack created"),
    onError: (error) => toast.error(error.message || "Failed to create pack"),
  })

  const updateMutation = trpc.linkPacks.update.useMutation({
    onSuccess: () => onMutationSuccess("Pack updated"),
    onError: (error) => toast.error(error.message || "Failed to update pack"),
  })

  const handleNameChange = (value: string) => {
    setName(value)
    if (!slugTouched) setSlug(slugify(value))
  }

  const updateLink = (index: number, field: keyof LinkRow, value: string) => {
    setLinks((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
  }

  const addLink = () => setLinks((prev) => [...prev, { url: "", label: "" }])

  const removeLink = (index: number) => {
    setLinks((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const cleanedLinks = links
      .map((row) => ({ url: row.url.trim(), label: row.label.trim() }))
      .filter((row) => row.url.length > 0)
      .map((row) => ({ url: normalizeUrl(row.url), label: row.label || undefined }))

    if (!name.trim() || !slug.trim()) {
      toast.error("Name and slug are required")
      return
    }
    if (cleanedLinks.length === 0) {
      toast.error("Add at least one URL")
      return
    }

    if (pack) {
      updateMutation.mutate({
        id: pack.id,
        slug: slug.trim(),
        name: name.trim(),
        description: description.trim() || undefined,
        emoji: emoji.trim() || undefined,
        links: cleanedLinks,
      })
    } else {
      createMutation.mutate({
        slug: slug.trim(),
        name: name.trim(),
        description: description.trim() || undefined,
        emoji: emoji.trim() || undefined,
        links: cleanedLinks,
      })
    }
  }

  const isSaving = createMutation.isLoading || updateMutation.isLoading

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <h2 className="text-lg font-semibold">{isEdit ? "Edit Pack" : "New LinkPack"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-md transition-colors" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto">
          <div className="flex gap-3">
            <div className="w-20">
              <label className="block text-sm font-medium mb-1">Emoji</label>
              <Input value={emoji} onChange={(e) => setEmoji(e.target.value)} placeholder="🚀" maxLength={8} />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">
                Name <span className="text-destructive">*</span>
              </label>
              <Input
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g., Morning Standup"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Slug <span className="text-destructive">*</span>
            </label>
            <Input
              value={slug}
              onChange={(e) => {
                setSlugTouched(true)
                setSlug(slugify(e.target.value))
              }}
              placeholder="morning-standup"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">Shareable link: /linkpacks/{slug || "your-slug"}</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this pack is for"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">
                URLs <span className="text-destructive">*</span>
              </label>
              <span className="text-xs text-muted-foreground">{links.length} link(s)</span>
            </div>
            <div className="space-y-2">
              {links.map((row, index) => (
                <div key={index} className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <Input
                    value={row.label}
                    onChange={(e) => updateLink(index, "label", e.target.value)}
                    placeholder="Label (optional)"
                    className="w-1/3"
                  />
                  <Input
                    value={row.url}
                    onChange={(e) => updateLink(index, "url", e.target.value)}
                    placeholder="https://example.com"
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => removeLink(index)}
                    className="p-2 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0 disabled:opacity-30"
                    disabled={links.length === 1}
                    aria-label="Remove URL"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addLink} className="mt-2">
              <Plus className="w-4 h-4 mr-1" />
              Add URL
            </Button>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" loading={isSaving} className="flex-1">
              {isEdit ? "Save Changes" : "Create Pack"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
